import React from "react";

type Language = "en" | "sv" | "de" | "fr" | "es";
type ThemeMode = "light" | "dark";

type Props = {
  email?: string | null;
  plan: "free" | "premium";
  language: Language;
  onChangeLanguage: (value: Language) => void;
  theme: ThemeMode;
  onChangeTheme: (value: ThemeMode) => void;
  onUpgrade: () => void;
  onSignOut: () => void;
};

export default function Settings({
  email,
  plan,
  language,
  onChangeLanguage,
  theme,
  onChangeTheme,
  onUpgrade,
  onSignOut,
}: Props) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Account
        </h2>

        <div className="mt-4 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Email
            </p>

            <p className="mt-1 text-sm text-gray-700">
              {email || "No email available"}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Current plan
            </p>

            <div className="mt-1 flex items-center gap-3">
              <span className="rounded-full bg-violet-50 px-3 py-1 text-sm font-semibold text-violet-700">
                {plan === "premium" ? "Premium" : "Free"}
              </span>

              {plan !== "premium" && (
                <button
                  type="button"
                  onClick={onUpgrade}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700"
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Preferences
        </h2>

        <div className="mt-4 max-w-md">
          <label className="text-xs font-semibold uppercase text-gray-400">
            Language
          </label>

          <select
            value={language}
            onChange={(e) =>
            onChangeLanguage(e.target.value as Language)
            }
            className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
          >
            <option value="en">English</option>
            <option value="sv">Swedish</option>
            <option value="de">German</option>
            <option value="fr">French</option>
            <option value="es">Spanish</option>
          </select>
        </div>
        <div className="mt-6">
        <label className="text-xs font-semibold uppercase text-gray-400">
            Appearance
        </label>

        <div className="mt-2 grid max-w-md grid-cols-2 gap-3">
            <button
            type="button"
            onClick={() => onChangeTheme("light")}
            className={
                theme === "light"
                ? "rounded-xl border border-violet-500 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700"
                : "rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700"
            }
            >
            Light
            </button>

            <button
            type="button"
            onClick={() => onChangeTheme("dark")}
            className={
                theme === "dark"
                ? "rounded-xl border border-violet-500 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700"
                : "rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700"
            }
            >
            Dark
            </button>
        </div>
        </div>
      </div>

      <div className="rounded-2xl border border-red-200 bg-white p-5 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Session
        </h2>

        <p className="mt-2 text-sm text-gray-500">
          Sign out of your JobTailorAI account on this device.
        </p>

        <button
          type="button"
          onClick={onSignOut}
          className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
        >
          Sign out
        </button>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900">
                Contact us
            </h2>

            <p className="mt-2 text-sm text-gray-500">
                Questions, feedback, or need help with JobTailorAI?
            </p>

            <a
                href="mailto:contact@jobtailorapp.now"
                className="mt-4 inline-flex rounded-lg border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-50"
            >
                contact@jobtailorapp.now
            </a>
            </div>
      </div>
    </div>
  );
}